import {
  getHubStructuredTemplate,
  type HubStructuredSlot,
} from '@dupe/generative-ui'
import {
  renderHubSection,
  shouldRenderHubSection,
  type HubKind,
  type HubRenderContext,
  type HubSection,
} from '~/src/components/reports/hubs/kit'

/**
 * Structured hub layout. The renderer owns the durable page shell and lets
 * generated copy fill bounded editorial sections.
 *
 * Templated kinds (see `HUB_STRUCTURED_TEMPLATES`) project their generated
 * sections onto a fixed, ordered slot list: every page of that kind renders the
 * same components in the same places, regardless of the order the generator
 * emitted them. Non-templated kinds (transactional, until its dedicated pass)
 * fall back to the legacy generator-ordered contract:
 *
 *   - hero (when present) is always first
 *   - sourcesSummary is normalized to #sources and always last
 *   - informational pages suppress product/methodology sections
 *
 * Width and divider treatment mirror SeoResearchReportPage so structured
 * hubs read as editorial pages, not narrow blog posts.
 */

const INFORMATIONAL_SUPPRESSED_SECTION_TYPES = new Set<HubSection['type']>([
  'methodologyCard',
  'topPicks',
  'productRail',
  'comparisonTable',
  'ctaBand',
  'reportGrid',
  'decisionBlock',
])

function buildSourcesSection(input: {
  context: HubRenderContext
  generatedSources?: Extract<HubSection, { type: 'sourcesSummary' }>
  anchorId?: string
}): HubSection | null {
  const sourceSummary = input.context.sourceSummary
  const totalSources =
    sourceSummary?.sourcesReviewed ??
    sourceSummary?.sourceLinks.length ??
    input.generatedSources?.totalSources ??
    0

  if (!input.generatedSources && totalSources <= 0) return null

  return {
    ...input.generatedSources,
    id: input.anchorId ?? 'sources',
    type: 'sourcesSummary',
    title: input.generatedSources?.title ?? 'Sources reviewed',
    totalSources,
  }
}

/**
 * Project generated sections onto a fixed slot template. Section array order
 * from the generator is ignored: the template owns placement. For each slot we
 * take the first generated section of the matching type (sourcesSummary is
 * synthesized from host context when absent) and pin it to the slot's position,
 * applying the slot's anchor id. Slots with no matching section collapse.
 */
function projectSectionsOntoTemplate(
  sections: HubSection[],
  template: readonly HubStructuredSlot[],
  context: HubRenderContext
): HubSection[] {
  const firstByType = new Map<string, HubSection>()
  for (const section of sections) {
    if (!firstByType.has(section.type)) firstByType.set(section.type, section)
  }

  const placed: HubSection[] = []
  for (const slot of template) {
    if (slot.sectionType === 'sourcesSummary') {
      const sources = buildSourcesSection({
        context,
        generatedSources: firstByType.get('sourcesSummary') as
          | Extract<HubSection, { type: 'sourcesSummary' }>
          | undefined,
        anchorId: slot.anchorId ?? slot.slotId,
      })
      if (sources) placed.push(sources)
      continue
    }

    const section = firstByType.get(slot.sectionType)
    if (!section) continue
    placed.push(slot.anchorId ? { ...section, id: slot.anchorId } : section)
  }

  return placed
}

function applyStructuredLayoutContract(
  sections: HubSection[],
  input: {
    context: HubRenderContext
    hubKind?: HubKind
  }
): HubSection[] {
  const hero = sections.find((section) => section.type === 'hero')
  const generatedSources = [...sections]
    .reverse()
    .find(
      (section): section is Extract<HubSection, { type: 'sourcesSummary' }> =>
        section.type === 'sourcesSummary'
    )
  let middle = sections.filter(
    (section) => section !== hero && section.type !== 'sourcesSummary'
  )

  if (input.hubKind === 'informational') {
    middle = middle.filter(
      (section) => !INFORMATIONAL_SUPPRESSED_SECTION_TYPES.has(section.type)
    )
  }

  const ordered: HubSection[] = []
  if (hero) ordered.push(hero)
  ordered.push(...middle)

  const sources = buildSourcesSection({
    context: input.context,
    generatedSources,
  })
  if (sources) ordered.push(sources)
  return ordered
}

export interface StructuredHubLayoutProps {
  sections: HubSection[]
  context: HubRenderContext
  hubKind?: HubKind
}

export function StructuredHubLayout({
  sections,
  context,
  hubKind,
}: StructuredHubLayoutProps) {
  const template = hubKind ? getHubStructuredTemplate(hubKind) : undefined
  const ordered = template
    ? projectSectionsOntoTemplate(sections, template, context)
    : applyStructuredLayoutContract(sections, { context, hubKind })
  const renderable = ordered.filter((section) =>
    shouldRenderHubSection(section, context)
  )
  const heroSection = renderable.find((section) => section.type === 'hero')
  const bodySections = renderable.filter((section) => section.type !== 'hero')

  return (
    <>
      {heroSection && (
        <div className='mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 pt-8 md:gap-10 md:px-10'>
          <div key={heroSection.id} className='scroll-mt-14'>
            {renderHubSection(heroSection, context)}
          </div>
        </div>
      )}

      <div className='mx-auto flex w-full max-w-6xl px-4 pb-20 pt-10 md:px-10'>
        {/* color-token-exception: section dividers darkened to match SeoResearchReportPage. border-border is too light at 8% opacity. */}
        <article className='flex min-w-0 flex-1 flex-col gap-12 [&>*+*]:border-t [&>*+*]:border-black/30 [&>*+*]:pt-12 [&>[id]]:scroll-mt-14'>
          {bodySections.map((section) => (
            <div key={section.id} className='min-w-0 scroll-mt-14'>
              {renderHubSection(section, context)}
            </div>
          ))}
        </article>
      </div>
    </>
  )
}
